import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getPendingUsers, approveUser, rejectUser, User } from '../api/client';

export function PendingUsersBanner() {
  const { token, user } = useAuth();
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  useEffect(() => {
    if (token && user?.is_admin) {
      loadPendingUsers();
    }
  }, [token, user?.is_admin]);

  const loadPendingUsers = async () => {
    if (!token) return;
    try {
      const users = await getPendingUsers(token);
      setPendingUsers(users);
      setDismissed(false);
    } catch (error) {
      console.error('Failed to load pending users:', error);
    }
  };

  const handleApprove = async (userId: number) => {
    if (!token) return;
    setActionLoading(userId);
    try {
      await approveUser(token, userId);
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Failed to approve user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId: number, username: string) => {
    if (!token) return;
    if (!window.confirm(`Are you sure you want to reject and delete the registration for "${username}"?`)) {
      return;
    }
    setActionLoading(userId);
    try {
      await rejectUser(token, userId);
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Failed to reject user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (!user?.is_admin || pendingUsers.length === 0 || dismissed) {
    return null;
  }

  return (
    <>
      <div style={{
        background: '#fff3cd',
        borderBottom: '1px solid #ffc107',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontFamily: 'system-ui'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#856404', fontWeight: 'bold' }}>
            {pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} pending approval
          </span>
          <button
            onClick={() => setShowModal(true)}
            style={{
              background: '#ffc107',
              border: 'none',
              color: '#212529',
              padding: '6px 16px',
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            Review
          </button>
        </div>
        <button
          onClick={() => setDismissed(true)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#856404',
            cursor: 'pointer',
            fontSize: '18px',
            padding: '4px 8px'
          }}
          title="Dismiss"
        >
          ×
        </button>
      </div>

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          fontFamily: 'system-ui'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px' }}>Pending User Approvals</h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ overflow: 'auto', flex: 1 }}>
              {pendingUsers.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  No pending users
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>User</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Registered</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingUsers.map(pendingUser => (
                      <tr key={pendingUser.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 'bold' }}>{pendingUser.name}</div>
                          <div style={{ fontSize: '14px', color: '#666' }}>@{pendingUser.username}</div>
                          {pendingUser.notification_email && (
                            <div style={{ fontSize: '14px', color: '#666' }}>{pendingUser.notification_email}</div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#666' }}>
                          {formatDate(pendingUser.created_at)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => handleApprove(pendingUser.id)}
                              disabled={actionLoading === pendingUser.id}
                              style={{
                                background: '#28a745',
                                border: 'none',
                                color: '#fff',
                                padding: '6px 16px',
                                cursor: actionLoading === pendingUser.id ? 'not-allowed' : 'pointer',
                                borderRadius: '4px',
                                fontSize: '14px',
                                opacity: actionLoading === pendingUser.id ? 0.7 : 1
                              }}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(pendingUser.id, pendingUser.username)}
                              disabled={actionLoading === pendingUser.id}
                              style={{
                                background: '#dc3545',
                                border: 'none',
                                color: '#fff',
                                padding: '6px 16px',
                                cursor: actionLoading === pendingUser.id ? 'not-allowed' : 'pointer',
                                borderRadius: '4px',
                                fontSize: '14px',
                                opacity: actionLoading === pendingUser.id ? 0.7 : 1
                              }}
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #ddd',
              textAlign: 'right'
            }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: '#6c757d',
                  border: 'none',
                  color: '#fff',
                  padding: '8px 20px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
